// packages/web/src/components/finances/FinancesOverview.tsx
import React, { useEffect, useState } from "react";
import { Col, Container, Form, Row, Spinner, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { DateRangePicker } from "react-dates";
import moment, { Moment } from "moment";
import styled from "styled-components";
import {
    fetchFinancesOverview,
    FinancesOverviewStats,
} from "@rpforms/shared/build/actions/financesOverview";

const StatGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 16px;
    row-gap: 32px;
    margin-top: 24px;

    @media (max-width: 991px) {
        grid-template-columns: 1fr 1fr;
    }
    @media (max-width: 575px) {
        grid-template-columns: 1fr;
    }
`;

const StatBox = styled.div`
    background: #fff;
    border: 1px solid #ced4da;
    padding: 40px 24px;
`;

const StatLabel = styled.div`
    font-size: 17px;
    font-weight: 600;
    color: #212529;
    margin-bottom: 12px;
`;

const StatValue = styled.div<{ color?: string }>`
    font-size: 38px;
    font-weight: 600;
    color: ${(props) => props.color || "#212529"};
`;

const TimeSelector = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .DateInput {
        width: 130px;
    }

    .DateInput_input {
        width: 100%;
    }

    .btn-group .btn {
        background-color: #e9ecef;
        border-color: #dee2e6;
        color: #495057;

        &.active,
        &:active {
            background-color: #6c757d;
            border-color: #6c757d;
            color: #fff;
        }

        &:hover:not(.active) {
            background-color: #dee2e6;
        }
    }
`;

type Preset = "1d" | "7d" | "30d" | "custom";

function getPresetRange(preset: Preset): [Moment, Moment] {
    const end = moment().subtract(1, "day").endOf("day");
    switch (preset) {
        case "1d":
            return [moment().subtract(1, "day").startOf("day"), end];
        case "7d":
            return [moment().subtract(7, "days").startOf("day"), end];
        case "30d":
            return [moment().subtract(30, "days").startOf("day"), end];
        default:
            return [moment().subtract(30, "days").startOf("day"), end];
    }
}

export const FinancesOverview = () => {
    const [preset, setPreset] = useState<Preset>("30d");
    const [startDate, setStartDate] = useState<Moment | null>(() => getPresetRange("30d")[0]);
    const [endDate, setEndDate] = useState<Moment | null>(() => getPresetRange("30d")[1]);
    const [focusedInput, setFocusedInput] = useState<"startDate" | "endDate" | null>(null);
    const [stats, setStats] = useState<FinancesOverviewStats | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isNet, setIsNet] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadStats = async (from: Moment, to: Moment) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchFinancesOverview(
                from.format("YYYY-MM-DD"),
                to.format("YYYY-MM-DD")
            );
            setStats(data);
        } catch (e) {
            console.error("Failed to load overview stats", e);
            const msg = e instanceof Error ? e.message : null;
            setError(msg || "Übersicht konnte nicht geladen werden");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (startDate && endDate) {
            loadStats(startDate, endDate);
        }
    }, [startDate, endDate]);

    const handlePreset = (value: Preset) => {
        setPreset(value);
        if (value !== "custom") {
            const [from, to] = getPresetRange(value);
            setStartDate(from);
            setEndDate(to);
        }
    };

    const handleDatesChange = ({
        startDate: sd,
        endDate: ed,
    }: {
        startDate: Moment | null;
        endDate: Moment | null;
    }) => {
        setPreset("custom");
        setStartDate(sd);
        setEndDate(ed);
    };

    return (
        <Container fluid>
            <Row style={{ marginTop: "2rem" }}>
                <Col>
                    <TimeSelector>
                        <ToggleButtonGroup
                            type="radio"
                            name="time-preset"
                            value={preset}
                            onChange={handlePreset}
                        >
                            <ToggleButton value="1d">Letzter Tag</ToggleButton>
                            <ToggleButton value="7d">Letzte 7 Tage</ToggleButton>
                            <ToggleButton value="30d">Letzte 30 Tage</ToggleButton>
                        </ToggleButtonGroup>
                        <DateRangePicker
                            startDate={startDate}
                            startDateId="overview_start"
                            endDate={endDate}
                            endDateId="overview_end"
                            onDatesChange={handleDatesChange}
                            focusedInput={focusedInput}
                            onFocusChange={setFocusedInput}
                            isOutsideRange={() => false}
                            displayFormat="DD.MM.YYYY"
                            startDatePlaceholderText="Von"
                            endDatePlaceholderText="Bis"
                            small
                        />
                        <Form.Check
                            type="checkbox"
                            label="Nettopreise anzeigen"
                            checked={isNet}
                            onChange={(e) => setIsNet(e.target.checked)}
                        />
                    </TimeSelector>
                </Col>
            </Row>

            {error ? (
                <Row style={{ marginTop: "2rem" }}>
                    <Col>
                        <div className="alert alert-danger">{error}</div>
                    </Col>
                </Row>
            ) : null}

            {isLoading ? (
                <Row style={{ marginTop: "3rem", justifyContent: "center" }}>
                    <Spinner animation="border" size="sm" />
                    <span style={{ marginLeft: "0.5rem" }}>Lade Übersicht...</span>
                </Row>
            ) : stats ? (
                <StatGrid>
                    <StatBox>
                        <StatLabel>
                            Rechnungen geschrieben ({isNet ? "Netto" : "Brutto"})
                        </StatLabel>
                        <StatValue>
                            {isNet
                                ? stats.invoices_net_total_formatted
                                : stats.invoices_gross_total_formatted}
                        </StatValue>
                    </StatBox>
                    <StatBox>
                        <StatLabel>
                            Angebote geschrieben ({isNet ? "Netto" : "Brutto"})
                        </StatLabel>
                        <StatValue>
                            {isNet
                                ? stats.offers_net_total_formatted
                                : stats.offers_gross_total_formatted}
                        </StatValue>
                    </StatBox>
                    <StatBox>
                        <StatLabel>Akzeptierte Angebote</StatLabel>
                        <StatValue color="#28a745">{stats.offers_accepted_count}</StatValue>
                    </StatBox>
                    <StatBox>
                        <StatLabel>Anzahl geschriebener Angebote</StatLabel>
                        <StatValue>{stats.offers_count}</StatValue>
                    </StatBox>
                    <StatBox>
                        <StatLabel>Anzahl geschriebener Rechnungen</StatLabel>
                        <StatValue>{stats.invoices_count}</StatValue>
                    </StatBox>
                    <StatBox>
                        <StatLabel>Erstellte Protokolle (ohne Angebot/Rechnung)</StatLabel>
                        <StatValue>{stats.protocols_count}</StatValue>
                    </StatBox>
                </StatGrid>
            ) : null}
        </Container>
    );
};
